/**
 * DateFilterPicker — A date picker control for the Analytics Hub filter panel.
 * Accepts a single DateSelectionState object as its source of truth.
 * Renders either a single date or a date range depending on the mode.
 * When the user clicks a date in the calendar, it emits a SINGLE_DATE selection.
 */

import React, { useMemo } from 'react';
import { Platform, View } from 'react-native';
import LhDatePicker from '../../../components/DatePicker/LhDatePicker';
import { useTheme } from '../../../theme';
import {
	DateSelectionState,
	createSingleDateSelection,
	formatDateFilterValue,
} from '../types/dateSelectionTypes';

interface DateFilterPickerProps {
	/** Single source of truth for the date selection */
	value: DateSelectionState;
	/** Called when the user interacts with the picker */
	onChange: (state: DateSelectionState) => void;
	/** Array of allowed ISO date strings from the backend */
	availableDates: string[];
	/** Whether the picker is disabled */
	disabled?: boolean;
	/** Placeholder text when no date is selected */
	placeholder?: string;
	/** Test ID for automation */
	testID?: string;
}

/** Convert an ISO date string (YYYY-MM-DD) to a Date object */
const isoToDate = (iso: string): Date => {
	const parts = iso.split('-');
	return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
};

/** Convert a Date object to an ISO date string (YYYY-MM-DD) */
const dateToIso = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const DateFilterPicker: React.FC<DateFilterPickerProps> = ({
	value,
	onChange,
	availableDates,
	disabled = false,
	placeholder = 'Select Date',
	testID,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';
	// Derive the Date object for the calendar's selected value
	const dateValue = useMemo<Date | null>(() => {
		if (value.mode === 'DATE_RANGE' && value.startDate) {
			return isoToDate(value.startDate);
		}
		if (value.mode === 'SINGLE_DATE' && value.selectedDate) {
			return isoToDate(value.selectedDate);
		}
		return null;
	}, [value]);

	// Derive the display text from DateSelectionState
	const displayText = useMemo<string | undefined>(() => {
		const text = formatDateFilterValue(value);
		return text || undefined;
	}, [value]);

	// Derive highlighted range for the calendar
	const highlightedRange = useMemo<{ start: Date; end: Date } | null>(() => {
		if (value.mode === 'DATE_RANGE' && value.startDate && value.endDate) {
			return {
				start: isoToDate(value.startDate),
				end: isoToDate(value.endDate),
			};
		}
		return null;
	}, [value]);

	// Determine min/max dates from available dates
	const minimumDate = useMemo<Date | undefined>(() => {
		if (highlightedRange) return highlightedRange.start;
		if (availableDates.length === 0) return undefined;
		return isoToDate(availableDates[0]);
	}, [availableDates, highlightedRange]);

	const maximumDate = useMemo<Date | undefined>(() => {
		if (highlightedRange) {
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			return highlightedRange.end < today ? highlightedRange.end : today;
		}
		if (availableDates.length === 0) return undefined;
		const lastAvailable = isoToDate(availableDates[availableDates.length - 1]);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return lastAvailable < today ? lastAvailable : today;
	}, [availableDates, highlightedRange]);

	const isFieldDisabled = disabled || availableDates.length === 0;

	// Handle date change from LhDatePicker — always produces a SINGLE_DATE selection
	const handleDateChange = (date: Date | null) => {
		if (!date) {
			onChange({ mode: 'SINGLE_DATE' });
			return;
		}
		const isoDate = dateToIso(date);
		onChange(createSingleDateSelection(isoDate));
	};

	return (
		<View testID={testID} style={{ width: '100%' }}>
			<LhDatePicker
					value={dateValue}
					onChange={handleDateChange}
					placeholder={placeholder}
					minimumDate={minimumDate}
					maximumDate={maximumDate}
					disabled={isFieldDisabled}
					colorScheme={isDark ? "dark" : "light"}
					iconColor={isDark ? colors.text.secondary : "#6A6D71"}
					disabledIconColor={isDark ? colors.text.disabled : "#B0B7C3"}
					displayText={displayText}
					highlightedRange={highlightedRange}
					enabledDates={highlightedRange ? undefined : availableDates}
				/>
		</View>
	);
};

export default DateFilterPicker;
